import styles from './PageBoundaryOverlay.module.scss';

interface PageBoundaryOverlayProps {
    /** Y positions (CSS px) of each interior page-height boundary, from `usePageBoundaries`. */
    boundaries: number[];
}

/**
 * Editor-only visual indicator (RECUR-27304, "see the pages"): every measured
 * page-height boundary gets the SAME marker — a thin seam line + a small
 * "Page N" chip — nothing else. RECUR-27304 "one consistent marker" (product
 * decision): this used to differ between a flush boundary (plain seam) and a
 * push-gap boundary (a constant grey gutter band + hatched fill + a "Blank in
 * PDF — Page N starts below" label); all of that was removed so every
 * boundary looks identical regardless of gap size or kind (block/row/line) —
 * this component no longer even receives that information, only the
 * boundary Ys.
 *
 * Editor↔PDF parity is preserved elsewhere: a push-gap's blank space is still
 * injected as real margin on the live block that starts the next page (see
 * `usePageBoundaries.ts`'s `applyPageGapStyles`), so editor page N still
 * aligns with PDF page N — only the ADDITIONAL band/label/gutter styling that
 * used to decorate that blank space was removed, so it now renders as plain
 * white instead.
 *
 * Purely presentational and `pointer-events: none` — a DOM sibling of
 * `<PlateContent>`, never a Slate/Plate node, so it never affects the
 * document value, selection, save/load, or PDF export (see `PlateEditor.tsx`
 * for the sibling mount, `usePageBoundaries.ts` for the boundary + gap math).
 */
export function PageBoundaryOverlay({ boundaries }: PageBoundaryOverlayProps) {
    if (boundaries.length === 0) return null;

    return (
        <div className={styles.overlay} aria-hidden="true">
            {boundaries.map((top, index) => {
                // Numbering: the k-th interior boundary labels the page that
                // BEGINS below it, so the first boundary reads "Page 2"
                // (page 1 has no top boundary).
                const pageNumber = index + 2;
                return (
                    <div key={top} className={styles.band} style={{ top: `${top}px` }}>
                        <span className={styles.label}>Page {pageNumber}</span>
                    </div>
                );
            })}
        </div>
    );
}
